<template>
  <div>
    <main-loader v-if="loading" class="pt-5" />
    <wat-sew-collisions-info
      v-else
      title-path="sidebar.fixedAssets.collisionsTitle"
      :custom-collisions-trees="collisionsTrees"
      :custom-collisions-trees-counts="collisionsTreesCounts"
      hover-color="255,0,0"
      :tabs="tabs"
      hide-buffer
    />
  </div>
</template>
<script>
import { call, get } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';
import WatSewCollisionsInfo from '@/components/WatSewCollisionsInfo';
export default {
  name: 'CustomCollisionsInfo',
  components: { MainLoader, WatSewCollisionsInfo },
  props: {
    collisionsData: {
      type: Array,
      default: () => {
        return [];
      },
      /*
      // Takes a list in a structure:
      [
        {
          dataSourceName: 'dataSourceName', // required
          attributeName: 'attributeName', // default takes id attribute of data source
          attributeValue: [1, 2], // required
          operator: '$IN' // default takes '$EQUAL'
        }
      ]
      */
    },
    additionalCollisions: {
      type: Array,
      default: () => {
        return [];
      },
      /*
      // Takes a list in a structure:
      [
        {
          id: 'data_source_name', // required
          name: 'Data Source Name', // required
          children: [
            {
              geometry: {},
              id: 'data_source_name-1'
              name: 'ID: 1'
            }
          ], //required
        }
      ]
      */
    },
    tabs: {
      type: Array,
      default: () => {
        return [
          {
            value: 'water',
            moduleName: 'WATER_DATA',
            settingName: 'water_data_module_enabled',
            scope: 'water',
          },
          {
            value: 'sewer',
            moduleName: 'SEWER_DATA',
            settingName: 'sewer_data_module_enabled',
            scope: 'sewer',
          },
        ];
      },
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    tabsScopes() {
      return this.tabs.map(tab => tab.scope);
    },
  },
  data: () => ({
    collisionsTrees: {},
    collisionsTreesCounts: {},
    loading: false,
  }),
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getFilterAttributeName(dataSourceName, attributeName) {
      const dataSource = this.dataSources[dataSourceName];
      const { attributes_use_datasource_qualified_names: qualifiedNames } = dataSource;
      const filterAttributeName = attributeName || dataSource.attributes_schema.id_name;
      return qualifiedNames ? filterAttributeName : `${dataSourceName}.${filterAttributeName}`;
    },
    getFilterExpression({ dataSourceName, attributeName = '', attributeValue, operator = '$EQUAL' }) {
      return {
        [operator]: {
          [this.getFilterAttributeName(dataSourceName, attributeName)]: attributeValue,
        },
      };
    },
    async setCollisionsTrees(collisionsData = this.collisionsData) {
      this.loading = true;
      try {
        const promisesData = collisionsData.map(collisionData => {
          const { attributeName = '', attributeValue, dataSourceName, operator = '$EQUAL' } = collisionData;
          const filters = this.getFilterExpression({ dataSourceName, attributeName, attributeValue, operator });
          const promise = this.getDataSourceFeatures({
            dataSource: dataSourceName,
            filters,
          });
          return { dataSourceName, promise };
        });
        const dataSourcesNames = promisesData.map(promiseData => promiseData.dataSourceName);
        const promises = promisesData.map(promiseData => promiseData.promise);
        const r = await Promise.all(promises);
        const rData = r.map((data, index) => {
          return { data, dataSourceName: dataSourcesNames[index] };
        });
        const collisionsTrees = {};
        const collisionsTreesCounts = {};
        this.tabsScopes.forEach(tab => {
          collisionsTrees[tab] = [];
          collisionsTreesCounts[tab] = 0;
        });
        rData.forEach(element => {
          const { data, dataSourceName } = element;
          const { crs } = data;
          const { scope, attributes_schema: attributesSchema, verbose_name: dataSourceVerboseName } = this.dataSources[
            dataSourceName
          ];
          const { desc_attribute_name: descAttributeName, id_name: idName } = attributesSchema;
          if (this.tabsScopes.includes(scope)) {
            const dataSourceTreeElement = {
              id: dataSourceName,
              name: dataSourceVerboseName,
              children: data.features.map(feature => {
                const { geometry, properties, id } = feature;
                properties[idName] = id;
                const descAttributeValue = properties[descAttributeName];
                const descAttributeVerboseName = attributesSchema.attributes.find(
                  attribute => attribute.name === descAttributeName
                )?.verbose_name;
                return {
                  id: `${dataSourceName}-${id}`,
                  geometry: { ...geometry, crs },
                  name: `${descAttributeVerboseName}: ${descAttributeValue}`,
                };
              }),
            };
            collisionsTrees[scope].push(dataSourceTreeElement);
            collisionsTreesCounts[scope] += dataSourceTreeElement?.children.length || 0;
          }
        });
        this.additionalCollisions.forEach(additionalCollision => {
          const { id: dataSourceName } = additionalCollision;
          const dataSource = this.dataSources[dataSourceName];
          const { scope } = dataSource;
          if (this.tabsScopes.includes(scope)) {
            const alreadyExistedDS = collisionsTrees[scope].find(dataSource => dataSource.id === dataSourceName);
            if (alreadyExistedDS) {
              additionalCollision.children.forEach(child => {
                if (!alreadyExistedDS.children.find(alreadyExistedChild => alreadyExistedChild.id === child.id)) {
                  alreadyExistedDS.children.push(child);
                }
              });
            } else {
              collisionsTrees[scope].push(additionalCollision);
            }
          }
        });
        this.collisionsTrees = collisionsTrees;
        this.collisionsTreesCounts = collisionsTreesCounts;
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    collisionsData: {
      immediate: true,
      handler(nV) {
        if (nV?.length) {
          this.setCollisionsTrees(nV);
        }
      },
    },
  },
};
</script>
